import '@formatjs/intl-listformat/polyfill.js'
import '@formatjs/intl-listformat/locale-data/pt.js'

import type { AppError, ErrorKeyNames, ParsedError } from './types'

export const getErrorMessage = (
  error: AppError,
  keysNames: ErrorKeyNames = {}
): ParsedError => {
  if (!error || typeof error !== 'object') {
    return {
      status: 500,
      statusCode: 500,
      code: 'UnknownError',
      name: 'UnknownError',
      title: 'Oops',
      message: `Ocorreu um erro inesperado ${JSON.stringify(error)}`,
      errors: [error]
    }
  }

  const err = error as Record<string, unknown>
  let errorTitle = 'Oops'
  let errorMessage = ''
  let errorStatus =
    (err.code as number | string | undefined) ??
    ((err.response as { status?: number } | undefined)?.status) ??
    500
  let errors: unknown = error

  if (err.response) {
    errorTitle = String((err.response as { status?: number }).status)

    const data = ((err.response as { data?: Record<string, unknown> }).data ??
      {}) as Record<string, unknown>
    const message = data.message as Record<string, unknown> | string | undefined

    errors =
      data.error_message ??
      data.errors ??
      data.error ??
      data.erro ??
      (typeof message === 'object' ? message?.errors ?? message?.error : undefined) ??
      (typeof message === 'object' ? message : data.message) ??
      'Ocorreu um erro inesperado. Entre em contato com o suporte.'
  } else if (err.request) {
    errorTitle = 'Sem internet'
    errors =
      'Sua conexão falhou durante o processo e não conseguimos continuar, verifique e tente novamente.'
  } else if (err.message) {
    errors = err.message
  }

  let errorsArray: string[] = []

  if (Array.isArray(errors)) {
    errorsArray = errors.map(item =>
      typeof item === 'object' && item !== null
        ? (item as { message?: string; text?: string }).message ||
          (item as { text?: string }).text ||
          JSON.stringify(item)
        : String(item)
    )
  } else if (typeof errors === 'object' && errors !== null) {
    for (const [errorKey, errorMessages] of Object.entries(
      errors as Record<string, unknown>
    )) {
      const omitName = keysNames?.[errorKey]?._omit_name
      const name = keysNames?.[errorKey]?._name || errorKey

      let formattedMessage = omitName ? '' : `${name}: `

      if (Array.isArray(errorMessages)) {
        const mappedMessages = errorMessages.map(item => {
          const translated =
            keysNames?.[errorKey]?.[String(item)] ??
            keysNames?.[String(item)]?._name
          return typeof translated === 'string' ? translated : String(item)
        })
        try {
          formattedMessage += new Intl.ListFormat('pt-BR').format(mappedMessages)
        } catch {
          formattedMessage += JSON.stringify(mappedMessages)
        }
      } else {
        formattedMessage +=
          typeof errorMessages === 'string'
            ? errorMessages
            : JSON.stringify(errorMessages)
      }

      errorsArray.push(formattedMessage)
    }
  } else {
    errorsArray = [typeof errors === 'string' ? errors : JSON.stringify(errors)]
  }

  try {
    errorMessage = new Intl.ListFormat('pt-BR').format(errorsArray)
  } catch {
    errorMessage = errorsArray.join(', ')
  }

  return {
    status: Number(errorStatus) || 500,
    statusCode: Number(errorStatus) || 500,
    code: String(err.name || errorMessage),
    name: String(err.name || errorMessage),
    title: errorTitle,
    message: errorMessage,
    errors
  }
}
